import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, ArrowRight, Server, CreditCard, FileText, Activity, Settings, User, HardDrive, Shield, Users, FolderOpen, Globe, Database } from 'lucide-react';
import { Input } from '../ui/Input';
import { cn } from '../../utils/cn';
import { apiClient } from '../../api/client';
import { ShortcutHint } from '../../hooks/useKeyboardShortcuts';

interface SearchResult {
  id: string;
  type: 'service' | 'order' | 'invoice' | 'action' | 'page';
  title: string;
  subtitle?: string;
  path: string;
  icon: React.ReactNode;
  category?: string;
}

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userServices, setUserServices] = useState<SearchResult[]>([]);
  const [recentOrders, setRecentOrders] = useState<SearchResult[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Load user's actual services when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUserData();
    }
  }, [isOpen]);

  const loadUserData = async () => {
    try {
      // Load user's services
      const servicesResponse = await apiClient.services.getServicesOverview();
      const services: SearchResult[] = servicesResponse.services.map((service: any) => {
        // Determine service type from product or metadata
        const productName = service.product?.name || service.name || 'Service';
        const isVPN = productName.toUpperCase().includes('VPN');
        const isSamba = productName.toUpperCase().includes('SAMBA');
        const isNFS = productName.toUpperCase().includes('NFS');
        
        return {
          id: `service-${service.id}`,
          type: 'service' as const,
          title: service.name || productName,
          subtitle: `Container ${service.ct_id || 'Pending'} - ${service.status}`,
          path: `/dashboard/services/${service.id}`,
          icon: isVPN ? <Shield className="h-4 w-4" /> :
                isSamba ? <FolderOpen className="h-4 w-4" /> :
                isNFS ? <HardDrive className="h-4 w-4" /> :
                <Server className="h-4 w-4" />,
          category: 'Your Services'
        };
      });
      setUserServices(services);

      // Load recent orders
      const ordersResponse = await apiClient.orders.listOrders();
      const orders: SearchResult[] = ordersResponse.slice(0, 5).map((order: any) => ({
        id: `order-${order.id}`,
        type: 'order' as const,
        title: `Order #${order.id}`,
        subtitle: `${order.product_name} - ${order.status}`,
        path: `/dashboard/billing`,
        icon: <CreditCard className="h-4 w-4" />,
        category: 'Recent Orders'
      }));
      setRecentOrders(orders);
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  // Quick actions for each service
  const getServiceActions = (services: SearchResult[]): SearchResult[] => {
    const actions: SearchResult[] = [];
    
    services.forEach(service => {
      const serviceId = service.id.replace('service-', '');
      const titleUpper = service.title.toUpperCase();
      const serviceType = titleUpper.includes('VPN') ? 'VPN' :
                         titleUpper.includes('SAMBA') ? 'SAMBA' :
                         titleUpper.includes('NFS') ? 'NFS' : '';
      
      if (serviceType === 'VPN') {
        actions.push({
          id: `vpn-users-${serviceId}`,
          type: 'action',
          title: `Manage VPN Users - ${service.title}`,
          subtitle: 'Add, remove or modify VPN users',
          path: `/dashboard/services/${serviceId}/vpn`,
          icon: <Users className="h-4 w-4" />,
          category: 'Service Actions'
        });
      } else if (serviceType === 'SAMBA') {
        actions.push({
          id: `samba-shares-${serviceId}`,
          type: 'action',
          title: `Manage Samba Shares - ${service.title}`,
          subtitle: 'Configure shared folders',
          path: `/dashboard/services/${serviceId}/samba`,
          icon: <FolderOpen className="h-4 w-4" />,
          category: 'Service Actions'
        });
      } else if (serviceType === 'NFS') {
        actions.push({
          id: `nfs-shares-${serviceId}`,
          type: 'action',
          title: `Manage NFS Shares - ${service.title}`,
          subtitle: 'Configure NFS exports',
          path: `/dashboard/services/${serviceId}/nfs`,
          icon: <Database className="h-4 w-4" />,
          category: 'Service Actions'
        });
      }
      
      // Common actions for all services
      actions.push({
        id: `users-${serviceId}`,
        type: 'action',
        title: `Users & Groups - ${service.title}`,
        subtitle: 'Manage service users and groups',
        path: `/dashboard/services/${serviceId}/users`,
        icon: <Users className="h-4 w-4" />,
        category: 'Service Actions'
      });
    });
    
    return actions;
  };

  // Default quick actions when nothing is searched
  const getQuickActions = (): SearchResult[] => {
    const actions: SearchResult[] = [];
    
    // Add user's services first
    if (userServices.length > 0) {
      actions.push(...userServices);
    }
    
    // Add service-specific actions
    const serviceActions = getServiceActions(userServices);
    if (serviceActions.length > 0) {
      actions.push(...serviceActions.slice(0, 3)); // Show top 3 actions
    }
    
    // Add general navigation
    actions.push({
      id: 'new-service',
      type: 'page',
      title: 'Order New Service',
      subtitle: 'Choose a plan and create new service',
      path: '/dashboard/plans',
      icon: <Server className="h-4 w-4" />,
      category: 'Quick Actions'
    });
    
    if (recentOrders.length > 0) {
      actions.push(...recentOrders.slice(0, 2)); // Show 2 recent orders
    }
    
    actions.push({
      id: 'billing',
      type: 'page',
      title: 'Billing & Invoices',
      subtitle: 'View orders and payment history',
      path: '/dashboard/billing',
      icon: <CreditCard className="h-4 w-4" />,
      category: 'Quick Actions'
    });
    
    return actions;
  };

  // Search for services, orders, etc.
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const searchResults: SearchResult[] = [];
      const query = searchQuery.toLowerCase();
      
      // Search through user's services
      const matchingServices = userServices.filter(service =>
        service.title.toLowerCase().includes(query) ||
        service.subtitle?.toLowerCase().includes(query)
      );
      searchResults.push(...matchingServices);
      
      // Search through service actions
      const allActions = getServiceActions(userServices);
      const matchingActions = allActions.filter(action =>
        action.title.toLowerCase().includes(query) ||
        action.subtitle?.toLowerCase().includes(query)
      );
      searchResults.push(...matchingActions);
      
      // Search through orders
      const matchingOrders = recentOrders.filter(order =>
        order.title.toLowerCase().includes(query) ||
        order.subtitle?.toLowerCase().includes(query)
      );
      searchResults.push(...matchingOrders);
      
      // Add navigation options if they match
      if ('billing'.includes(query) || 'invoice'.includes(query) || 'payment'.includes(query)) {
        searchResults.push({
          id: 'billing-nav',
          type: 'page',
          title: 'Billing & Invoices',
          subtitle: 'View all orders and invoices',
          path: '/dashboard/billing',
          icon: <CreditCard className="h-4 w-4" />,
          category: 'Navigation'
        });
      }
      
      if ('new'.includes(query) || 'order'.includes(query) || 'create'.includes(query)) {
        searchResults.push({
          id: 'new-service-nav',
          type: 'page',
          title: 'Order New Service',
          subtitle: 'Choose a plan and create new service',
          path: '/dashboard/plans',
          icon: <Server className="h-4 w-4" />,
          category: 'Navigation'
        });
      }
      
      if ('activity'.includes(query) || 'log'.includes(query) || 'history'.includes(query)) {
        searchResults.push({
          id: 'activity-nav',
          type: 'page',
          title: 'Activity Logs',
          subtitle: 'View activity history',
          path: '/dashboard/activity',
          icon: <Activity className="h-4 w-4" />,
          category: 'Navigation'
        });
      }
      
      if ('setting'.includes(query) || 'profile'.includes(query) || 'account'.includes(query)) {
        searchResults.push({
          id: 'settings-nav',
          type: 'page',
          title: 'Account Settings',
          subtitle: 'Manage your account',
          path: '/dashboard/settings',
          icon: <Settings className="h-4 w-4" />,
          category: 'Navigation'
        });
      }
      
      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [userServices, recentOrders]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (query.trim()) {
      debounceTimer.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, performSearch]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle modal close with better cross-browser support
  useEffect(() => {
    if (!isOpen) return;
    
    const handleModalClose = (e: KeyboardEvent) => {
      // Only handle Escape when modal is actually open and focused
      // Don't preventDefault to allow browser native ESC behavior
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    // Use regular event listener, not capture phase
    document.addEventListener('keydown', handleModalClose);
    return () => document.removeEventListener('keydown', handleModalClose);
  }, [isOpen, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const allItems = query ? results : getQuickActions();
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % allItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + allItems.length) % allItems.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (allItems[selectedIndex]) {
            navigate(allItems[selectedIndex].path);
            onClose();
          }
          break;
        // Remove Escape handling here - it's handled in the other useEffect
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, query, results, selectedIndex, navigate, onClose, userServices]);

  const handleItemClick = (item: SearchResult) => {
    navigate(item.path);
    onClose();
  };

  const displayItems = query ? results : getQuickActions();
  
  // Group items by category
  const groupedItems = displayItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  // Don't render anything if not open
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-background border rounded-lg shadow-lg">
        {/* Search Input */}
        <div className="flex items-center border-b p-4">
            <Search className="h-5 w-5 text-muted-foreground mr-3" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={t('search.placeholder', 'Search services, orders, or navigate...')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 border-0 bg-transparent focus:ring-0 focus:outline-none text-base"
            />
            <button
              onClick={onClose}
              className="ml-3 p-1 hover:bg-muted rounded transition-colors"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto p-2">
            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                {t('search.searching', 'Searching...')}
              </div>
            ) : displayItems.length > 0 ? (
              <div className="space-y-3">
                {Object.entries(groupedItems).map(([category, items]) => (
                  <div key={category}>
                    <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase">
                      {category}
                    </div>
                    <div className="space-y-1">
                      {items.map((item) => {
                        const globalIndex = displayItems.indexOf(item);
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleItemClick(item)}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left',
                              globalIndex === selectedIndex
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted'
                            )}
                          >
                            <div className="flex-shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{item.title}</div>
                              {item.subtitle && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {item.subtitle}
                                </div>
                              )}
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : query ? (
              <div className="p-4 text-center text-muted-foreground">
                {t('search.noResults', 'No results found for')} "{query}"
              </div>
            ) : null}
        </div>

        {/* Footer with shortcuts */}
        <div className="border-t px-4 py-3 flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px] font-semibold">↑↓</kbd>
                {t('search.navigate', 'Navigate')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px] font-semibold">↵</kbd>
                {t('search.select', 'Select')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px] font-semibold">ESC</kbd>
                {t('search.close', 'Close')}
              </span>
            </div>
            <ShortcutHint shortcut="SEARCH" label="" />
        </div>
      </div>
    </div>
  );
}